import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $convertFromMarkdownString } from '@lexical/markdown';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  COMMAND_PRIORITY_NORMAL,
  createCommand,
  type LexicalEditor,
} from 'lexical';

import type { TextOperationResult, TextOperationScope } from '../../../../domain';
import { WRITER_LAB_TRANSFORMERS } from '../lexicalConfig';

export interface AIInsertPayload {
  /** Result blocks from a TextOperationResult — Lexical-compatible JSON nodes (v3.0.1). */
  blocks: object[];
  /** Plain-text output; the v3.0 rehydration path splits this into paragraphs. */
  text?: string;
  scope: TextOperationScope;
  replaceRange?: { startOffset: number; endOffset: number };
  /** For lineage display (v3.0.1 BlockMarkupPlugin will use this). */
  operationId?: string;
}

export const AI_INSERT_COMMAND = createCommand<AIInsertPayload>('AI_INSERT');

/** Split a plain-text block into non-empty paragraph strings (blank-line delimited). */
function splitParagraphs(text: string): string[] {
  return text
    .split(/\n{2,}/)
    .map(p => p.trim())
    .filter(Boolean);
}

function appendParagraph(text: string): void {
  const para = $createParagraphNode();
  if (text) para.append($createTextNode(text));
  $getRoot().append(para);
}

/**
 * Core AI-insert mutation body. `$`-prefixed because it MUST run inside an
 * `editor.update()` (Lexical convention) — it reads `$getRoot()`/`$getSelection()`
 * of the active editor state. Exported so tests can drive it (and set up a live
 * selection) within a single update, sidestepping the headless-editor quirk that
 * selection isn't retained across update boundaries.
 *
 * v3.0 rehydrates `payload.text` into paragraphs; block-JSON rehydration from
 * `payload.blocks` lands in v3.0.1.
 */
export function $applyAiInsert(payload: AIInsertPayload): void {
  const root = $getRoot();
  const text = payload.text ?? '';

  if (payload.scope === 'Document') {
    // Replace the entire document content.
    //
    // The models emit Markdown — headings, bold, lists. Wrapping that in plain
    // text nodes rendered the syntax literally ("## Introduction", "**bold**")
    // instead of formatting it. Parse it into real Lexical nodes using the same
    // curated transformer set the MarkdownShortcutPlugin already uses, so typed
    // and generated formatting land as the same node types.
    //
    // `$convertFromMarkdownString` clears and repopulates the root itself, so it
    // replaces the manual clear + paragraph loop entirely.
    if (!text.trim()) {
      root.clear();
      root.append($createParagraphNode());
      return;
    }
    $convertFromMarkdownString(text, WRITER_LAB_TRANSFORMERS);
    // A trailing empty paragraph gives the caret somewhere to land after
    // generated content that ends in a block node (heading, list, quote).
    const last = root.getLastChild();
    if (!last || last.getType() !== 'paragraph') {
      root.append($createParagraphNode());
    }
    return;
  }

  if (payload.scope === 'Selection') {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      selection.insertText(text);
      return;
    }
    // Selection lost — fall back to append below.
  }

  // Block or fallback: insert at the cursor when there's a range selection,
  // otherwise append to the end of the document.
  const selection = $getSelection();
  if ($isRangeSelection(selection)) {
    const para = $createParagraphNode();
    para.append($createTextNode(text));
    $insertNodes([para]);
  } else {
    // Split on blank lines so a multi-paragraph append doesn't collapse into a
    // single paragraph. (These non-Document scopes are reserved for v3.0.1
    // Selection editing; they stay plain-text — only the Document path, which
    // every shipped operation uses, parses Markdown.)
    const paragraphs = splitParagraphs(text);
    if (paragraphs.length === 0) appendParagraph(text);
    else paragraphs.forEach(appendParagraph);
  }
}

/**
 * Wrap `$applyAiInsert` in an editor update. Pass `{ discrete: true }` in tests
 * to flush synchronously before reading the editor state.
 */
export function runAiInsert(
  editor: LexicalEditor,
  payload: AIInsertPayload,
  options?: { discrete?: true },
): void {
  editor.update(() => $applyAiInsert(payload), options);
}

/**
 * Lexical plugin: registers the AI_INSERT command so operation panels can splice
 * an operation's output into the editor. Renders nothing.
 */
export const AIInsertPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand<AIInsertPayload>(
      AI_INSERT_COMMAND,
      payload => {
        runAiInsert(editor, payload);
        return true;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor]);

  return null;
};

/**
 * Convenience for operation panels: take a TextOperationResult and dispatch the
 * AI_INSERT command. No-op when the result carries no `result` payload.
 */
export function dispatchAiInsert(
  editor: LexicalEditor,
  result: TextOperationResult,
  scope: TextOperationScope,
): void {
  if (!result.result) return;
  editor.dispatchCommand(AI_INSERT_COMMAND, {
    blocks: result.result.outputBlocks ?? [],
    text: result.result.outputText,
    scope,
    replaceRange: result.result.replaceRange ?? undefined,
    operationId: result.operationId,
  });
}
